import React, { useMemo, useState } from "react";
import { Alert, Divider, Stack, Typography } from "@mui/material";
import {
  useCreateManualSocialOpportunity,
  useCreateSocialPost,
  useDismissSocialOpportunity,
  useMarkSocialOpportunityUsed,
  useSaveSocialOpportunity,
} from "../../hooks/useAdminSocial";
import ManualSeedForm from "./ManualSeedForm";
import OpportunityCard from "./OpportunityCard";

const V1_RECIPE_IDS = new Set([
  "daily_debate",
  "community_pulse",
  "similarity_surprise",
  "stat_shock",
  "player_comparison",
]);

const RECIPE_BY_TYPE = {
  community_today: "daily_debate",
  community_close_vote: "community_pulse",
  community_landslide: "community_pulse",
  community_high_volume: "community_pulse",
  community_high_comments: "community_pulse",
  community_high_flips: "community_pulse",
  similarity_surprise: "similarity_surprise",
  similarity_famous_pair: "similarity_surprise",
  stat_shock: "stat_shock",
  stat_era_contrast: "stat_shock",
  player_comparison: "player_comparison",
};

const recipeIdForOpportunity = (opportunity) => {
  if (V1_RECIPE_IDS.has(opportunity.recipeId)) return opportunity.recipeId;

  const fallbackRecipeId = RECIPE_BY_TYPE[opportunity.type];
  return V1_RECIPE_IDS.has(fallbackRecipeId) ? fallbackRecipeId : null;
};

const missingRecipeReason = (opportunity) =>
  recipeIdForOpportunity(opportunity)
    ? null
    : `No v1 recipe accepts ${opportunity.type} yet.`;

const postIdFromResult = (result) =>
  result?.data?.post?.postId ||
  result?.data?.post?.id ||
  result?.data?.post?._id ||
  result?.data?.postId ||
  null;

const OpportunityFeed = ({
  opportunities = [],
  isLoading = false,
  onPostCreated,
}) => {
  const create = useCreateSocialPost();
  const dismiss = useDismissSocialOpportunity();
  const save = useSaveSocialOpportunity();
  const markUsed = useMarkSocialOpportunityUsed();
  const manual = useCreateManualSocialOpportunity();
  const [actionError, setActionError] = useState(null);

  const rankedOpportunities = useMemo(
    () => [...opportunities].sort((a, b) => (b.score || 0) - (a.score || 0)),
    [opportunities]
  );
  const actionsDisabled =
    create.isLoading ||
    dismiss.isLoading ||
    save.isLoading ||
    markUsed.isLoading;

  const runAction = async (action, payload) => {
    setActionError(null);
    const result = await action(payload);
    if (!result?.success) setActionError(result?.error || "Action failed");
    return result;
  };

  const createPost = async (opportunity) => {
    const recipeId = recipeIdForOpportunity(opportunity);
    if (!recipeId) return;

    const result = await runAction(create.createPost, {
      opportunityId: opportunity.opportunityKey,
      recipeId,
    });
    const postId = postIdFromResult(result);
    if (result?.success && postId) onPostCreated?.(postId);
  };

  return (
    <Stack spacing={2.5}>
      {actionError && <Alert severity="error">{actionError}</Alert>}
      {isLoading && <Typography>Loading opportunities…</Typography>}
      {!isLoading && rankedOpportunities.length === 0 && (
        <Typography color="text.secondary">
          No active opportunities.
        </Typography>
      )}
      {rankedOpportunities.map((opportunity) => (
        <OpportunityCard
          actionsDisabled={actionsDisabled}
          createPostDisabled={!recipeIdForOpportunity(opportunity)}
          createPostDisabledReason={missingRecipeReason(opportunity)}
          key={opportunity.opportunityKey}
          onCreatePost={createPost}
          onDismiss={(id) => runAction(dismiss.dismissOpportunity, id)}
          onMarkUsed={(id) => runAction(markUsed.useOpportunity, id)}
          onSave={(id) => runAction(save.saveOpportunity, id)}
          opportunity={opportunity}
        />
      ))}
      <Divider />
      <ManualSeedForm
        disabled={manual.isLoading}
        onSubmit={(seed) =>
          runAction(manual.createManualOpportunity, seed)
        }
      />
    </Stack>
  );
};

export default OpportunityFeed;
